import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PredictedSetlist as PredictedSetlistType, PredictedSetlistSong } from '../types/predictedSetlist';

interface Props {
  prediction: PredictedSetlistType;
  selectedSongs: Set<string>;
  onSelectionChange: (selected: Set<string>) => void;
  onOrderChange: (songs: PredictedSetlistSong[]) => void;
}

interface SongItemProps {
  song: PredictedSetlistSong;
  isSelected: boolean;
  onToggle: () => void;
}

const SortableSongItem: React.FC<SongItemProps> = ({ song, isSelected, onToggle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: song.name,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px',
    borderBottom: '1px solid #f0f0f0',
    background: isDragging ? '#f9fff9' : 'white',
    cursor: 'grab',
    listStyle: 'none',
  };

  return (
    <li ref={setNodeRef} style={style} {...attributes}>
      <span {...listeners} style={{ cursor: 'grab', color: '#aaa', fontSize: '16px' }} aria-label="drag handle">⠿</span>
      <input
        type="checkbox"
        checked={isSelected}
        onChange={onToggle}
        aria-label={`Select ${song.name}`}
        onClick={e => e.stopPropagation()}
      />
      <span style={{ flex: 1, fontSize: '14px' }}>{song.name}</span>
      <span style={{ fontSize: '12px', color: '#888' }}>×{song.appearanceCount}</span>
      {song.isCover && (
        <span style={{ fontSize: '11px', background: '#eee', padding: '1px 6px', borderRadius: '3px', color: '#666' }}>
          cover
        </span>
      )}
      {song.isTape && (
        <span style={{ fontSize: '11px', background: '#eef', padding: '1px 6px', borderRadius: '3px', color: '#666' }}>
          tape
        </span>
      )}
    </li>
  );
};

export const PredictedSetlist: React.FC<Props> = ({
  prediction,
  selectedSongs,
  onSelectionChange,
  onOrderChange,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = prediction.songs.findIndex(s => s.name === active.id);
    const newIndex = prediction.songs.findIndex(s => s.name === over.id);
    onOrderChange(arrayMove(prediction.songs, oldIndex, newIndex));
  };

  const handleToggle = (songName: string) => {
    const next = new Set(selectedSongs);
    if (next.has(songName)) {
      next.delete(songName);
    } else {
      next.add(songName);
    }
    onSelectionChange(next);
  };

  return (
    <div>
      <h3 style={{ margin: '0 0 4px', fontSize: '16px' }}>Predicted setlist for {prediction.artistName}</h3>
      <p style={{ margin: '0 0 12px', fontSize: '12px', color: '#888' }}>
        Based on {prediction.setlistsAnalysed} setlists · {prediction.songs.length} songs
      </p>

      {prediction.songs.length === 0 ? (
        <p style={{ color: '#888', fontSize: '14px' }}>No songs found for this artist.</p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={prediction.songs.map(s => s.name)} strategy={verticalListSortingStrategy}>
            <ul style={{ margin: 0, padding: 0 }}>
              {prediction.songs.map(song => (
                <SortableSongItem
                  key={song.name}
                  song={song}
                  isSelected={selectedSongs.has(song.name)}
                  onToggle={() => handleToggle(song.name)}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};
